import { useSuspenseQuery } from '@tanstack/react-query';
import { OpeningDetailResponse, SourcingService } from 'api-clients/monolith';
import _range from 'lodash/range';
import { Suspense, useEffect, useMemo, useState } from 'react';

import { useJobPostingContext } from '@/contexts/job-posting-context';
import { queryKeys } from '@/query-keys';

import { JobDetailFormState } from '../types';
import { JobCard } from './job-card';
import { RefreshJobDataBanner } from './refresh-job-data-banner';
import { SummaryView } from './summary-view';

/**
 * Fields on `JobDetailFormState` that are managed by monolith — refreshed on every
 * successful refetch so the user sees the latest values after editing in monolith.
 * Everything else (`title`, `description`, `stateCode`, `isExpanded`) is user-managed
 * and preserved across refetches.
 */
function serverManagedFields(
  opening: OpeningDetailResponse,
): Pick<
  JobDetailFormState,
  | 'remote'
  | 'industry'
  | 'jobHours'
  | 'jobType'
  | 'experience'
  | 'locationAddress'
  | 'postalCode'
  | 'city'
> {
  return {
    remote: opening.remote,
    industry: opening.job_industry,
    jobHours: opening.job_hours,
    jobType: opening.job_type,
    experience: opening.years_of_experience,
    locationAddress: opening.remote ? 'remote' : opening.location_address,
    postalCode: opening.postal_code,
    city: opening.city,
  };
}

function JobList() {
  const { selectedOpeningIds, hookForm } = useJobPostingContext();
  const { setValue, getValues, watch } = hookForm;
  const jobDetailsList = watch('jobDetails');
  const channels = watch('channels');
  const [showRefreshBanner, setShowRefreshBanner] = useState(false);

  // Only fetch job details for openings that are actually selected in at least
  // one channel's `openingBudgets`. Openings the user removed from every
  // channel are dropped here so we don't waste a request on them.
  const filteredSelectedOpeningIds = useMemo(() => {
    const openingIdsInChannels = new Set<string>();
    for (const channel of Object.values(channels ?? {})) {
      for (const openingId of Object.keys(channel.openingBudgets ?? {})) {
        openingIdsInChannels.add(openingId);
      }
    }
    return selectedOpeningIds.filter(id => openingIdsInChannels.has(id));
  }, [selectedOpeningIds, channels]);

  const {
    data: { openings },
  } = useSuspenseQuery({
    queryKey: [...queryKeys.jobPosting.jobDetails, filteredSelectedOpeningIds],
    queryFn: () =>
      SourcingService.postInternalApiSourcingOpeningsOpeningsByExternalIds({
        opening_external_ids: filteredSelectedOpeningIds,
      }),
  });

  /**
   * Reconcile the `jobDetails` form state with the latest `openings` payload
   * whenever it changes (initial mount and every successful refetch).
   */
  useEffect(() => {
    if (openings.length === 0) return;

    const existingJobDetails = getValues('jobDetails');

    const next = openings.reduce<Record<string, JobDetailFormState>>(
      (acc, opening) => {
        const existing = existingJobDetails?.[opening.external_id];
        const serverFields = serverManagedFields(opening);

        return {
          ...acc,
          [opening.external_id]: existing
            ? { ...existing, ...serverFields }
            : {
                ...serverFields,
                slug: opening.slug,
                title: opening.title,
                description: opening.job_description ?? '',
                stateCode: opening.state_code ?? undefined,
                isExpanded: openings.length === 1,
              },
        };
      },
      // Preserve any orphaned entries already in form state (e.g. an opening
      // removed via "Remove from posting" — UI still filters those out at render time).
      { ...existingJobDetails },
    );

    setValue('jobDetails', next, { shouldValidate: true });
  }, [openings, setValue, getValues]);

  /** Filter the openings to remove the ones that are deleted from the form state */
  const filteredOpenings = useMemo(() => {
    return openings.filter(opening =>
      Object.keys(jobDetailsList ?? {}).includes(opening.external_id),
    );
  }, [openings, jobDetailsList]);

  return (
    <div className="flex w-full flex-col gap-3">
      {showRefreshBanner && (
        <RefreshJobDataBanner onClose={() => setShowRefreshBanner(false)} />
      )}

      {filteredOpenings.map(opening => (
        <JobCard
          key={opening.external_id}
          opening={opening}
          onRequestRefreshBanner={() => setShowRefreshBanner(true)}
        />
      ))}
    </div>
  );
}

export function JobDetails() {
  return (
    <Suspense
      fallback={
        <>
          {_range(10).map(index => (
            <SummaryView.Skeleton key={index} />
          ))}
        </>
      }
    >
      <JobList />
    </Suspense>
  );
}
